import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Reveal } from "./Reveal";
import { useSiteData, useSectionDesign } from "../../context/SiteData";
import { optImg } from "../../lib/img";

const Counter = ({ to, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: "easeOut",
      onUpdate: (v) => setVal(Math.round(v * 10) / 10),
    });
    return () => controls.stop();
  }, [inView, to]);

  const display = Number.isInteger(to) ? Math.round(val) : val.toFixed(1);
  return (
    <span ref={ref} data-testid={`about-counter-${suffix || to}`}>
      {display}
      {suffix}
    </span>
  );
};

const STATS = [
  { value: 20, suffix: "", label: "Years of experience" },
  { value: 38, suffix: "K", label: "Happy guests" },
  { value: 10, suffix: "/10", label: "Average rating" },
  { value: 16, suffix: "", label: "Swiss destinations" },
];

export const About = () => {
  const { content } = useSiteData();
  const sec = useSectionDesign("about");
  return (
  <section id="about" className="relative py-24 sm:py-32 bg-[#f8fafc] overflow-hidden" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
      <Reveal className="relative">
        <div className="rounded-[2.5rem] overflow-hidden shadow-[0_40px_80px_-30px_rgba(11,19,43,0.3)]" data-testid="about-image-main">
          <img
            src={optImg(content.about_image, 900)}
            alt="Scenic Swiss landscape on a Voyage Switzerland journey"
            loading="lazy" decoding="async"
            className="w-full h-[340px] sm:h-[440px] object-cover"
          />
        </div>
        <div
          className="absolute -bottom-10 -right-2 sm:right-8 w-40 sm:w-56 rounded-3xl overflow-hidden border-8 border-[#f8fafc] shadow-2xl hidden sm:block"
          data-testid="about-image-secondary"
        >
          <img
            src={optImg(content.about_image2, 900)}
            alt="Travellers enjoying a scenic guided tour in Switzerland"
            loading="lazy" decoding="async"
            className="w-full h-40 sm:h-48 object-cover"
          />
        </div>
      </Reveal>

      <div>
        <Reveal>
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-[var(--brand)]" data-testid="about-eyebrow">
            {content.about_eyebrow}
          </p>
          <h2 className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0b132b]" data-testid="about-title">
            {sec.text("headline", content.about_title)}
          </h2>
          <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed" data-testid="about-copy">
            {sec.text("subtext", content.about_copy)}
          </p>
        </Reveal>

        <div className="mt-10 grid grid-cols-2 gap-5" data-testid="about-stats">
          {((content.about_stats && content.about_stats.length) ? content.about_stats : STATS).map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div className="rounded-3xl bg-white border border-slate-100 p-6 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]">
                <p className="font-display font-extrabold text-3xl sm:text-4xl text-[var(--brand)]">
                  <Counter to={Number(s.value ?? s.to)} suffix={s.suffix} />
                </p>
                <p className="mt-1.5 text-sm font-semibold text-slate-500">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
  );
};
